import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resetParts } from "../../../redux/partSlice";
import { resetSections } from "../../../redux/sectionSlice";
import { resetSteps } from "../../../redux/stepSlice";
import Loader from "../../../common/Loader";
import VersionHistoryCopyAppBar from "../../../common/VersionHistoryCopyAppBar";
import SelectRevisionTable from "../../NodeActions/assets/components/SelectRevision/SelectRevisionTable";

const ProcedureCopySelectProcedure = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.parts);
  const location = useLocation();

  const {
    title,
    partNumber,
    revision: fromRevision,
    partId,
    siblingOrder,
    fromSectionId,
    fromStepId,
    fromSubstepId,
    type,
    description,
    elementType,
    id,
    section,
    steps,
    sections,
    richTextsByStep,
    attachmentsByStep,
    canvasesByStep,
    formsByStep,
    videosByStep,
    status,
    sourceSubstepIndex,
    sourceStepId,
    sourceNodeId,
    targetSubstepIndex,
    targetStepId,
    targetNodeId,
    sourceNodeIndex,
    sourceSectionIndex,
    sourceStepIndex,
    revisionId,
    fromTreeId,
    fromSiblingOrder,
    step,
    fromStepSiblingOrder,
    fromStepTreeId,
    fromSubstepTreeId,
    fromSubstepSiblingOrder,
    elementId,
    elementTreeId,
    substep,
    forms,
    attachments,
    canvases,
    richTexts,
    videos,
    nodeType,
    node,
    substepsByStep,
    childTreeId,
    fromSubstepLetter,
    // Add newData to access the actual fetched element data
    newData,
  } = location.state || {};

  // Debug logging to understand the routing context
  useEffect(() => {
  }, [location.state, elementType, title]);

  // Handle revision selection - extracted from table row click handler
  const handleRevisionSelect = ({ group, revision }) => {
    // Construct the node data based on the elementType
    let constructedNode = node;
    if (!constructedNode && elementType) {
      if (
        elementType.toLowerCase().includes("attachment") &&
        (newData || attachments)
      ) {
        // For attachments, construct node from newData or attachments data
        const attachmentData =
          newData ||
          (Array.isArray(attachments)
            ? attachments[0]
            : attachments);
        constructedNode = {
          type: "attachment",
          data: attachmentData,
          elementType: "attachment",
          title: title,
          description: description,
          status: status,
          id: elementId || id,
          // Include the original attachments array for compatibility
          attachments: [attachmentData], // Wrap in array for consistency
        };
      } else if (
        elementType.toLowerCase().includes("rich") &&
        (newData || richTexts)
      ) {
        // For rich text, construct node from newData or richTexts data
        const richTextData =
          newData ||
          (Array.isArray(richTexts)
            ? richTexts[0]
            : richTexts);
        constructedNode = {
          type: "richText",
          data: richTextData,
          elementType: "richText",
          title: title,
          description: description,
          status: status,
          id: elementId || id,
          richTexts: [richTextData], // Wrap in array for consistency
        };
      } else if (
        elementType.toLowerCase().includes("video") &&
        (newData || videos)
      ) {
        // For videos, construct node from newData or videos data
        const videoData =
          newData ||
          (Array.isArray(videos) ? videos[0] : videos);
        constructedNode = {
          type: "video",
          data: videoData,
          elementType: "video",
          title: title,
          description: description,
          status: status,
          id: elementId || id,
          videos: [videoData], // Wrap in array for consistency
        };
      } else if (
        elementType.toLowerCase().includes("canvas") &&
        (newData || canvases)
      ) {
        // For canvases, construct node from newData or canvases data
        const canvasData =
          newData ||
          (Array.isArray(canvases)
            ? canvases[0]
            : canvases);
        constructedNode = {
          type: "canvas",
          data: canvasData,
          elementType: "canvas",
          title: title,
          description: description,
          status: status,
          id: elementId || id,
          canvases: [canvasData], // Wrap in array for consistency
        };
      } else if (
        elementType.toLowerCase().includes("form") &&
        (newData || forms)
      ) {
        // For forms, construct node from newData or forms data
        const formData =
          newData ||
          (Array.isArray(forms) ? forms[0] : forms);
        constructedNode = {
          type: "form",
          data: formData,
          elementType: "form",
          title: title,
          description: description,
          status: status,
          id: elementId || id,
          forms: [formData], // Wrap in array for consistency
        };
      }
    }

    // Debug logging for node construction

    // Final fallback: if we still don't have a node but have elementType and title,
    // create a basic node structure
    if (!constructedNode && elementType && title) {
      constructedNode = {
        type: elementType.toLowerCase(),
        elementType: elementType,
        title: title,
        description: description || "",
        status: status || "",
        id: elementId || id || "",
        // Include all available data, prioritizing newData
        attachments: newData || attachments,
        richTexts: newData || richTexts,
        videos: newData || videos,
        canvases: newData || canvases,
        forms: newData || forms,
      };
    }

    const state = {
      revision: revision.label,
      siblingOrder,
      partId: group.partId,
      revisionId: revision.id || "",
      fromSectionId,
      fromStepId,
      fromSubstepId,
      fromRevision,
      type,
      description: group.description,
      fromPartNumber: partNumber,
      toRevision: revision.label || "",
      toPartNumber: group.partNumber,
      toType: group.type,
      toRevisionId: revision.id || "",
      fromPartId: partId,
      fromRevisionId: revisionId,
      elementType,
      id,
      title,
      partNumber,
      status: group.status,
      steps,
      step,
      sourceStepId,
      sourceNodeId,
      nodeType,
      node: constructedNode, // Use the constructed node
      targetStepId,
      targetNodeId,
      fromTreeId,
      copyTitle: location.state.copyTitle || "",
      fromSiblingOrder,
      fromStepSiblingOrder,
      fromStepTreeId,
      fromSubstepTreeId,
      fromSubstepSiblingOrder,
      elementId,
      elementTreeId,
      richTextsByStep,
      attachmentsByStep,
      canvasesByStep,
      formsByStep,
      videosByStep,
      section,
      substepsByStep,
      fromSubstepLetter,
      richTexts,
      sections,
      substep,
      attachments,
      canvases,
      forms,
      stepsBySection: steps,
      videos,
      sourceSubstepIndex,
      sourceNodeId,
      targetSubstepIndex,
      targetStepId,
      targetNodeId,
      sourceNodeIndex,
      sourceSectionIndex,
      sourceStepIndex,
      childTreeId,
    };

    // Determine the correct route based on context, elementType and title
    let targetRoute = "/procedure-copy"; // fallback

    // Get context from location state
    const {
      isSectionElement,
      isSection,
      isStep,
      isSubstep,
      isPart,
    } = location.state || {};

    // Priority order: procedure/revision copy first, then context flags, then elementType, then title
    // If this is a procedure/revision copy (isPart is true), always go to procedure-copy
    if (isPart) {
      targetRoute = "/procedure-copy";
    } else if (isSubstep) {
      targetRoute = "/procedure-substep-copy";
    } else if (isStep) {
      targetRoute = "/procedure-step-copy";
    } else if (isSection) {
      // Only route to section-copy if it's actually a section
      targetRoute = "/procedure-section-copy";
    } else if (
      elementType?.toLowerCase().includes("subs") ||
      title.includes("Substep")
    ) {
      targetRoute = "/procedure-substep-copy";
    } else if (
      elementType?.toLowerCase() === "step" ||
      title.includes("Step")
    ) {
      targetRoute = "/procedure-step-copy";
    } else if (
      elementType?.toLowerCase() === "section" ||
      title.includes("Section")
    ) {
      targetRoute = "/procedure-section-copy";
    } else if (
      elementType?.toLowerCase().includes("rich") ||
      elementType?.toLowerCase().includes("video") ||
      elementType?.toLowerCase().includes("canvas") ||
      elementType?.toLowerCase().includes("form") ||
      elementType?.toLowerCase().includes("attachment") ||
      title.toLowerCase().includes("rich") ||
      title.toLowerCase().includes("video") ||
      title.toLowerCase().includes("canvas") ||
      title.toLowerCase().includes("form") ||
      title.toLowerCase().includes("attachment")
    ) {
      targetRoute = "/procedure-child-copy";
    }


    // Reset Redux data to prevent old revision from being displayed
    dispatch(resetSections());
    dispatch(resetSteps());
    dispatch(resetParts());

    navigate(targetRoute, { state });
  };

  return (
    <div>
      {loading && <Loader text="Loading documents..." />}
      <VersionHistoryCopyAppBar
        title={`${
          location.state.copyTitle
            ? "Select the Document"
            : title.includes(partNumber)
            ? `Copy & Replacing ${type} from ${partNumber} ${fromRevision}`
            : `Copying ${title} from ${partNumber} ${fromRevision}`
        } ${location.state.copyTitle ? "" : " . Select the Document"}`}
        showTablesDropdown={false}
        subheadingBelowTitle={
          location.state.copyTitle ? location.state.copyTitle : ""
        }
        showCancelButton={true}
        showBackButton={false}
        showProfileButton={false}
      />
      
      {/* Use the new SelectRevisionTable component */}
      <div style={{ marginTop: "24px" }}>
        <SelectRevisionTable
          sourcePartNumber={partNumber}
          onRevisionSelect={handleRevisionSelect}
          fromRevision={fromRevision}
        />
      </div>
    </div>
  );
};

export default ProcedureCopySelectProcedure;
